import { useEffect, useRef, useState } from 'react';

import { AiChatActivityPhase } from 'constants/aiChat';

import { initialDisplay } from './utils';

/**
 * Sticky-runningTool selector for the activity bar.
 *
 * Tools dispatched in rapid succession (~100–250ms apart) cause the underlying
 * phase to oscillate between `RUNNING_TOOL` and short `WRITING` flashes
 * (the BE briefly streams content between consecutive tool calls). A blanket
 * debounce drops the tool labels entirely. Instead we surface every
 * `RUNNING_TOOL` immediately, then keep the most recent tool label visible
 * for `holdMs` after it ends so brief inter-tool flashes do not override it.
 *
 * @param {string} phase Current phase from the activity selector.
 * @param {string|null} toolName Current running tool friendly name.
 * @param {number} holdMs How long to keep the last tool label after it ends.
 * @returns {{ phase: string, toolName: (string|null) }}
 */
const useStickyRunningToolPhase = (phase, toolName, holdMs) => {
  const [display, setDisplay] = useState(() => initialDisplay(phase, toolName));
  const lastRunningToolEndAtRef = useRef(0);
  const lastRunningToolNameRef = useRef(null);

  useEffect(() => {
    if (phase === AiChatActivityPhase.RUNNING_TOOL) {
      lastRunningToolEndAtRef.current = 0;
      lastRunningToolNameRef.current = toolName ?? lastRunningToolNameRef.current;
      setDisplay({ phase, toolName });

      return;
    }

    if (
      display.phase === AiChatActivityPhase.RUNNING_TOOL &&
      lastRunningToolEndAtRef.current === 0
    ) {
      lastRunningToolEndAtRef.current = Date.now();
    }

    const elapsed = Date.now() - lastRunningToolEndAtRef.current;
    const remaining = holdMs - elapsed;
    const shouldHold =
      lastRunningToolEndAtRef.current > 0 && remaining > 0 && lastRunningToolNameRef.current;

    if (!shouldHold) {
      lastRunningToolEndAtRef.current = 0;
      lastRunningToolNameRef.current = null;
      setDisplay({ phase, toolName });

      return;
    }

    setDisplay({
      phase: AiChatActivityPhase.RUNNING_TOOL,
      toolName: lastRunningToolNameRef.current,
    });

    const timeoutId = setTimeout(() => {
      lastRunningToolEndAtRef.current = 0;
      lastRunningToolNameRef.current = null;
      setDisplay({ phase, toolName });
    }, remaining);

    return () => clearTimeout(timeoutId);
  }, [phase, toolName, holdMs, display.phase]);

  return display;
};

export default useStickyRunningToolPhase;
